import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./total_estimate.css";
import {
  Plus,
  Search,
  Filter,
  Trash2,
  Download,
  Printer,
  RefreshCcw,
} from "lucide-react";

export default function TotalEstimates() {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  const [estimates, setEstimates] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState([
    { amount: "₦0.00", description: "Total Estimates" },
    { amount: 0, description: "Paid Estimates" },
    { amount: "₦0.00", description: "Total Value of Paid Estimates" },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // 1) load all estimates for this company (UNCHANGED)
  const fetchEstimates = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("estimates")
      .select("*")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching estimates:", error);
    } else {
      setEstimates(data || []);
      // initial filter is "all"
      setFiltered(data || []);
      updateStats(data || []);
      setCurrentPage(1);
    }
  }, [companyId]);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  // 2) recompute stats (UNCHANGED semantics)
  function updateStats(data) {
    const total = data.reduce((sum, e) => sum + (e.total || e.amount || 0), 0);
    const paid = data.filter((e) => (e.status || "").toLowerCase() === "paid");
    const paidCount = paid.length;
    const paidTotal = paid.reduce(
      (sum, e) => sum + (e.total || e.amount || 0),
      0
    );

    setStats([
      { amount: `₦${total.toLocaleString()}`, description: "Total Estimates" },
      { amount: paidCount, description: "Paid Estimates" },
      {
        amount: `₦${paidTotal.toLocaleString()}`,
        description: "Total Value of Paid Estimates",
      },
    ]);
  }

  // 3) search + status filter (client-side; keeps original search behavior)
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    const next = estimates.filter((est) => {
      const matchesText = (est.customer_name || "").toLowerCase().includes(q);
      const s = (est.status || "unknown").toLowerCase();
      const matchesStatus = statusFilter === "all" ? true : s === statusFilter;
      return matchesText && matchesStatus;
    });
    setFiltered(next);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, estimates]);

  // 4) delete (same DB scoping)
  const handleDelete = async (id) => {
    const { error } = await supabase
      .from("estimates")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) {
      console.error("Error deleting estimate:", error);
    } else {
      const next = filtered.filter((e) => e.id !== id);
      setFiltered(next);
      setEstimates(estimates.filter((e) => e.id !== id));
      updateStats(estimates.filter((e) => e.id !== id));
    }
  };

  // 5) navigation (UNCHANGED)
  const handleAdd = () => navigate("/estimate");

  // —— UI helpers ——
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSlice = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  const refresh = () => fetchEstimates();

  const exportCSV = () => {
    // client-only export of the current filtered list
    const csvRows = [
      ["Customer", "Estimate #", "Date", "Due", "Status", "Total (NGN)"].join(
        ","
      ),
      ...filtered.map((e) =>
        [
          (e.customer_name || "").replaceAll(",", " "),
          e.document_number || e.Estimates_ID || "",
          e.date || "",
          e.expiryDate || "",
          e.status || "",
          (e.total || 0).toString(),
        ].join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kostpadi-estimates.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const printList = () => window.print();

  const badgeClass = (status) => {
    const s = (status || "unknown").toLowerCase();
    if (s === "paid") return "badge paid";
    if (s === "open" || s === "pending") return "badge open";
    if (s === "overdue") return "badge overdue";
    return "badge unknown";
  };

  return (
    <div className="estimates-page">
      {/* HERO */}
      <header className="estimates-hero">
        <div>
          <p className="estimates-eyebrow">Sales & Quotations</p>

          <h1>Total Estimates</h1>

          <p>
            Review, search and manage all customer estimates and quotations
            across your company.
          </p>
        </div>

        <div className="estimates-hero-actions">
          <button className="estimate-btn soft" onClick={refresh}>
            <RefreshCcw size={16} />
            Refresh
          </button>

          <button className="estimate-btn soft" onClick={printList}>
            <Printer size={16} />
            Print
          </button>

          <button className="estimate-btn soft" onClick={exportCSV}>
            <Download size={16} />
            Export
          </button>

          <button className="estimate-btn primary" onClick={handleAdd}>
            <Plus size={16} />
            Create Estimate
          </button>
        </div>
      </header>

      {/* STATS */}
      <section className="estimate-stats-grid">
        {stats.map((s, i) => (
          <div className="estimate-stat-card" key={i}>
            <div className="estimate-stat-icon">📘</div>

            <div>
              <strong>{s.amount}</strong>
              <span>{s.description}</span>
            </div>
          </div>
        ))}
      </section>

      {/* FILTERS */}
      <section className="estimate-panel">
        <div className="estimate-panel-head">
          <div>
            <h3>Search & Filters</h3>
            <p>Find estimates by customer, status or document details.</p>
          </div>
        </div>

        <div className="estimate-filters">
          <div className="estimate-search">
            <Search size={16} />

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer name..."
            />
          </div>

          <div className="estimate-tabs">
            <span className="estimate-tabs-label">
              <Filter size={14} />
              Status
            </span>

            {["all", "open", "paid", "overdue"].map((s) => (
              <button
                key={s}
                className={`estimate-tab ${statusFilter === s ? "active" : ""}`}
                onClick={() => setStatusFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className="estimate-panel">
        <div className="estimate-panel-head">
          <div>
            <h3>Estimate Records</h3>
            <p>
              Showing {filtered.length} estimate
              {filtered.length !== 1 ? "s" : ""}.
            </p>
          </div>
        </div>

        {pageSlice.length ? (
          <div className="estimate-cards">
            {pageSlice.map((est) => (
              <article key={est.id} className="estimate-card">
                <div className="estimate-card-top">
                  <div>
                    <h3 title={est.customer_name || "—"}>
                      {est.customer_name || "—"}
                    </h3>

                    <span className="estimate-doc-number">
                      #{est.document_number || est.Estimates_ID || "—"}
                    </span>
                  </div>

                  <span className={badgeClass(est.status)}>
                    {est.status || "Unknown"}
                  </span>
                </div>

                <div className="estimate-card-body">
                  <div className="estimate-meta-grid">
                    <div className="estimate-meta-box">
                      <span>Date</span>
                      <strong>{est.date || "—"}</strong>
                    </div>

                    <div className="estimate-meta-box">
                      <span>Expiry Date</span>
                      <strong>{est.expiryDate || "—"}</strong>
                    </div>

                    <div className="estimate-meta-box total">
                      <span>Total Amount</span>
                      <strong>₦{(est.total || 0).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                <div className="estimate-card-actions">
                  <button
                    className="estimate-btn danger"
                    onClick={() => handleDelete(est.id)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="estimate-empty">
            <div className="estimate-empty-icon">📄</div>

            <h3>No estimates found</h3>

            <p>
              Create your first estimate to begin tracking quotations and
              customer proposals.
            </p>

            <button className="estimate-btn primary" onClick={handleAdd}>
              <Plus size={16} />
              Create Estimate
            </button>
          </div>
        )}

        {/* PAGINATION */}
        {filtered.length > pageSize && (
          <div className="estimate-pagination">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`estimate-page-btn ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
